/**
 * Requests supersede one another within a scope — one module instance, or one
 * view's interactions — so the scope must identify its owner unambiguously.
 */
export function moduleActionScope(
  moduleId: string,
  instanceKey: string | undefined,
): string {
  return `module\0${moduleId}\0${instanceKey ?? ""}`;
}

export function viewInteractionScope(viewId: string): string {
  return `view\0${viewId}`;
}

function actionAbortKey(scope: string, actionName: string): string {
  return `${scope}\0${actionName}`;
}

const controllers = new Map<string, AbortController>();

/**
 * Cancel any in-flight request for the same action in `scope` and return a
 * fresh {@link AbortSignal} for the new request (design: latest interaction wins).
 */
export function supersedeActionRequest(scope: string, actionName: string): AbortSignal {
  const key = actionAbortKey(scope, actionName);
  controllers.get(key)?.abort();
  const next = new AbortController();
  controllers.set(key, next);
  return next.signal;
}

/**
 * Remove the registry entry when the request that created {@link signal} is done,
 * so a completed request does not block future aborts.
 */
export function releaseActionAbortSlot(
  scope: string,
  actionName: string,
  signal: AbortSignal,
): void {
  const key = actionAbortKey(scope, actionName);
  const current = controllers.get(key);
  if (current?.signal === signal) {
    controllers.delete(key);
  }
}
